import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, ViewChild, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs';
import { AuthService } from '@core/service/auth.service';
import { PersonaService } from '@shared/service';

interface AdminNavItem {
  label: string;
  desc: string;
  path: string;
  iconKey: string;
}

/**
 * Admin console sidebar (mockup `<aside class="admin-side">`, shown in `.amode`).
 * The mockup's a-console tabs (overview/vendors/customers/finance) become routes.
 */
@Component({
  selector: 'app-admin-sidebar',
  standalone: true,
  styleUrl: './admin-sidebar.component.scss',
  imports: [CommonModule, RouterModule],
  templateUrl: './admin-sidebar.component.html',
  
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminSidebarComponent implements AfterViewInit {
  private readonly auth = inject(AuthService);
  private readonly persona = inject(PersonaService);
  private readonly router = inject(Router);

  /** The nav strip — a horizontal scroller once the sidebar collapses (see the scss `tablet` block). */
  @ViewChild('navEl') private navEl?: ElementRef<HTMLElement>;

  constructor() {
    this.router.events.pipe(filter(e => e instanceof NavigationEnd), takeUntilDestroyed())
      .subscribe(() => this.revealActive());
  }

  ngAfterViewInit() { this.revealActive(); }

  /**
   * Scroll the active section into view in the collapsed top-nav. On a phone the strip only fits
   * two or three pills, so the section you are actually on ("Finance", last but one) sits off the
   * end of a row with no affordance saying it scrolls. A no-op on the desktop sidebar, which is a
   * vertical column with nothing to scroll.
   */
  private revealActive() {
    const nav = this.navEl?.nativeElement;
    if (!nav) return;
    // routerLinkActive stamps `.on` during the change detection that follows NavigationEnd.
    setTimeout(() => {
      if (nav.scrollWidth <= nav.clientWidth) return;
      nav.querySelector('.anav.on')?.scrollIntoView({ inline: 'center', block: 'nearest' });
    });
  }

  /** Title line in the footer card (mirrors the vendor mecard's vendor name). */
  readonly adminLabel = computed(() => this.persona.hasRole('super_admin') ? 'Super Admin' : 'Admin');

  readonly items: AdminNavItem[] = [
    { label: 'Overview',  desc: 'Platform snapshot',      path: '/admin/overview',  iconKey: 'grid' },
    { label: 'Vendors',   desc: 'Verify & inspect',       path: '/admin/vendors',   iconKey: 'store' },
    { label: 'Customers', desc: 'Bookings & escrow',      path: '/admin/customers', iconKey: 'people' },
    { label: 'Finance',   desc: 'Escrow, fees, payouts',  path: '/admin/finance',   iconKey: 'wallet' },
    { label: 'WhatsApp',  desc: 'Message templates',      path: '/admin/whatsapp',  iconKey: 'whatsapp' },
  ];

  logout() { void this.auth.logout(); }
}
